import React, { useCallback, useEffect, useState } from "react";
import {
  ArrowPathIcon,
  ArrowRightIcon,
  PhoneIcon,
  ChatBubbleLeftIcon,
  ClockIcon,
  ExclamationCircleIcon,
  CheckCircleIcon,
  ArrowUturnLeftIcon,
  ChartBarIcon,
  TagIcon,
  XCircleIcon,
  BriefcaseIcon,
} from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";
import AccessDenied from "../common/AccessDenied";
import { getMyRoles } from "../../services/userService";
import { getOutreachStats } from "../../services/outreachMonitorService";

const ALLOWED_ROLES = ["admin", "data-analyst"];

const PERIOD_LABELS = {
  "1h": "Last Hour",
  "4h": "Last 4 Hours",
  "1d": "Last 24h",
  "3d": "Last 3 Days",
  "1w": "Last 7 Days",
  "2w": "Last 14 Days",
};

const PERIODS = ["1h", "4h", "1d", "3d", "1w", "2w"];

function StatCard({ label, value, sub, color = "text-blue-400", icon: Icon }) {
  return (
    <div className="card-glass p-4 flex items-center gap-3">
      {Icon && (
        <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-gray-800 flex items-center justify-center">
          <Icon className={`w-5 h-5 ${color}`} />
        </div>
      )}
      <div>
        <p className="text-xs text-gray-400">{label}</p>
        <p className={`text-2xl font-bold ${color}`}>{value}</p>
        {sub && <p className="text-xs text-gray-500 mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

const TOPICS_PAGE_SIZE = 15;
const TOPICS_MAX = 30;

function TopicsTable({ rows }) {
  const [expanded, setExpanded] = useState(false);
  if (!rows || rows.length === 0) {
    return <p className="text-xs text-gray-500 italic">No data</p>;
  }
  const capped = rows.slice(0, TOPICS_MAX);
  const visible = expanded ? capped : capped.slice(0, TOPICS_PAGE_SIZE);
  const hasMore = capped.length > TOPICS_PAGE_SIZE;
  return (
    <>
      <table className="w-full text-xs">
        <thead>
          <tr className="text-gray-400">
            <th className="text-left py-1 font-medium">Topic</th>
            <th className="text-right py-1 font-medium text-purple-400">
              Calls
            </th>
            <th className="text-right py-1 font-medium text-green-400">
              Texts
            </th>
            <th className="text-right py-1 font-medium text-gray-300">Total</th>
          </tr>
        </thead>
        <tbody>
          {visible.map((row, idx) => (
            <tr key={idx} className="border-t border-gray-800">
              <td
                className="py-1.5 text-gray-300 max-w-[200px] truncate"
                title={row.topic_name}
              >
                {row.topic_name}
              </td>
              <td className="py-1.5 text-right font-mono font-semibold text-gray-100">
                {row.calls}
              </td>
              <td className="py-1.5 text-right font-mono font-semibold text-gray-100">
                {row.texts}
              </td>
              <td className="py-1.5 text-right font-mono font-semibold text-gray-400">
                {row.total}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {hasMore && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="mt-2 text-xs text-blue-400 hover:text-blue-300 font-medium transition-colors"
        >
          {expanded
            ? "− Show less"
            : `+ Show ${capped.length - TOPICS_PAGE_SIZE} more`}
        </button>
      )}
    </>
  );
}

function ClosedReasonsTable({ rows }) {
  if (!rows || rows.length === 0) {
    return <p className="text-xs text-gray-500 italic">No data</p>;
  }
  return (
    <table className="w-full text-xs">
      <thead>
        <tr className="text-gray-400">
          <th className="text-left py-1 font-medium">Close Reason</th>
          <th className="text-right py-1 font-medium">Count</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row, idx) => (
          <tr key={idx} className="border-t border-gray-800">
            <td className="py-1.5 text-gray-300">{row.reason}</td>
            <td className="py-1.5 text-right font-mono font-semibold text-gray-100">
              {row.count}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function formatDuration(seconds) {
  if (!seconds) return "0s";
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

function formatTimestamp(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

const REFRESH_INTERVAL_MS = 15 * 60 * 1000; // 15 minutes

export default function OutreachMonitor() {
  const navigate = useNavigate();
  const [hasAccess, setHasAccess] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const [activePeriod, setActivePeriod] = useState("1h");

  const fetchStats = useCallback(async ({ bypassCache = false } = {}) => {
    setLoading(true);
    setError(null);
    try {
      const data = await getOutreachStats({ bypassCache });
      console.log(data);
      setStats(data?.data ?? data);
      setLastRefreshed(new Date());
    } catch (err) {
      console.error("Error fetching outreach stats:", err);
      setError("Failed to load stats. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  const viewSessionWithErroredJobs = (assignmentId) => {
    navigate(`/sessions?assignment_id=${assignmentId}`);
  };

  useEffect(() => {
    getMyRoles().then((roles) => {
      const ok = roles.some((role) => ALLOWED_ROLES.includes(role));
      setHasAccess(ok);
      if (ok) fetchStats();
    });
  }, [fetchStats]);

  // Auto-refresh every 15 minutes (bypass cache)
  useEffect(() => {
    if (!hasAccess) return;
    const interval = setInterval(
      () => fetchStats({ bypassCache: true }),
      REFRESH_INTERVAL_MS,
    );
    return () => clearInterval(interval);
  }, [hasAccess, fetchStats]);

  if (hasAccess === null) return null; // still checking
  if (!hasAccess) return <AccessDenied />;

  const calls = stats?.calls;
  const texts = stats?.texts;

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-100">Outreach Monitor</h1>
          {lastRefreshed && (
            <p className="text-xs text-gray-500 mt-0.5">
              Last updated: {formatTimestamp(lastRefreshed.toISOString())}{" "}
              &nbsp;·&nbsp; Auto-refreshes every 15 min
            </p>
          )}
        </div>
        <button
          onClick={() => fetchStats({ bypassCache: true })}
          disabled={loading}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-gray-700 hover:bg-gray-600 text-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <ArrowPathIcon
            className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`}
          />
          {loading ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      {error && (
        <div className="flex items-center gap-2 rounded-lg bg-red-900/30 border border-red-700 p-3 text-sm text-red-300">
          <ExclamationCircleIcon className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {stats && (
        <>
          {/* ── Row 1: Headline numbers ── */}
          <section>
            <div className="flex items-center gap-3 mb-3">
              <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                {activePeriod
                  ? PERIOD_LABELS[activePeriod]
                  : "Active Now & Post-Processing"}
              </h2>
              <div className="flex items-center gap-1">
                {PERIODS.map((p) => (
                  <button
                    key={p}
                    onClick={() =>
                      setActivePeriod(activePeriod === p ? null : p)
                    }
                    className={`px-2 py-0.5 text-xs rounded-md font-medium transition-colors ${
                      activePeriod === p
                        ? "bg-blue-600 text-white"
                        : "bg-gray-700 text-gray-400 hover:bg-gray-600 hover:text-gray-200"
                    }`}
                  >
                    {PERIOD_LABELS[p].replace("Last ", "")}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <StatCard
                label={
                  activePeriod
                    ? `Calls (${PERIOD_LABELS[activePeriod]})`
                    : "Active Calls"
                }
                value={
                  activePeriod
                    ? (calls?.by_period?.[activePeriod] ?? "—")
                    : (calls?.active ?? "—")
                }
                icon={PhoneIcon}
                color="text-purple-400"
              />
              <StatCard
                label={
                  activePeriod
                    ? `Texts (${PERIOD_LABELS[activePeriod]})`
                    : "Active Texts"
                }
                value={
                  activePeriod
                    ? (texts?.by_period?.[activePeriod] ?? "—")
                    : (texts?.active ?? "—")
                }
                icon={ChatBubbleLeftIcon}
                color="text-green-400"
              />
              <StatCard
                label="Tasks Created (Last 14 Days)"
                value={stats?.post_processing?.tasks_created ?? "—"}
                icon={CheckCircleIcon}
                color="text-teal-400"
              />
              <StatCard
                label="Opt-Outs (Last 14 Days)"
                value={stats?.post_processing?.opt_outs ?? "—"}
                icon={ArrowUturnLeftIcon}
                color="text-red-400"
              />
            </div>
          </section>

          {/* ── Row 2: Call Quality ── */}
          <section>
            <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
              Call Quality
            </h2>
            <div className="card-glass p-4">
              <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-700">
                <ClockIcon className="w-4 h-4 text-cyan-400" />
                <h3 className="text-sm font-semibold text-gray-200">
                  Outgoing Activity &amp; Avg Duration
                </h3>
              </div>
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-gray-400">
                    <th className="text-left py-1 font-medium">Period</th>
                    <th className="text-right py-1 font-medium text-orange-400">
                      Outgoing Calls
                    </th>
                    <th className="text-right py-1 font-medium text-cyan-400">
                      Avg Duration (Outgoing Calls)
                    </th>
                    <th className="text-right py-1 font-medium text-green-400">
                      Outgoing Texts
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {PERIODS.map((p) => {
                    const dur = calls?.avg_duration_by_period?.[p];
                    return (
                      <tr key={p} className="border-t border-gray-800">
                        <td className="py-1.5 text-gray-400">
                          {PERIOD_LABELS[p]}
                        </td>
                        <td className="py-1.5 text-right font-mono font-semibold text-gray-100">
                          {calls?.by_period?.[p] ?? "—"}
                        </td>
                        <td className="py-1.5 text-right font-mono font-semibold text-gray-100">
                          {dur != null ? formatDuration(dur) : "—"}
                        </td>
                        <td className="py-1.5 text-right font-mono font-semibold text-gray-100">
                          {texts?.by_period?.[p] ?? "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>

          {/* ── Row 3: Volume ── */}
          <section>
            <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
              <ChartBarIcon className="w-4 h-4" />
              Assignment Volume
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Volume */}
              <div className="card-glass p-4">
                <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-700">
                  <PhoneIcon className="w-4 h-4 text-blue-400" />
                  <h3 className="text-sm font-semibold text-gray-200">
                    Volume of Outreaches
                  </h3>
                </div>
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-gray-400">
                      <th className="text-left py-1 font-medium">Period</th>
                      <th className="text-right py-1 font-medium text-purple-400">
                        Calls
                      </th>
                      <th className="text-right py-1 font-medium text-green-400">
                        Texts
                      </th>
                      <th className="text-right py-1 font-medium text-gray-400">
                        Total
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {PERIODS.map((p) => (
                      <tr key={p} className="border-t border-gray-800">
                        <td className="py-1.5 text-gray-400">
                          {PERIOD_LABELS[p]}
                        </td>
                        <td className="py-1.5 text-right font-mono font-semibold text-gray-100">
                          {calls?.by_period?.[p] ?? "—"}
                        </td>
                        <td className="py-1.5 text-right font-mono font-semibold text-gray-100">
                          {texts?.by_period?.[p] ?? "—"}
                        </td>
                        <td className="py-1.5 text-right font-mono font-semibold text-gray-400">
                          {calls?.by_period?.[p] != null &&
                          texts?.by_period?.[p] != null
                            ? calls.by_period[p] + texts.by_period[p]
                            : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Minimum Success Criteria (all-time) */}
              <div className="card-glass p-4">
                <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-700">
                  <CheckCircleIcon className="w-4 h-4 text-teal-400" />
                  <h3 className="text-sm font-semibold text-gray-200">
                    Minimum Success Criteria{" "}
                    <span className="text-xs text-gray-500 font-normal">
                      (last 14 days)
                    </span>
                  </h3>
                </div>
                <div className="flex items-center justify-center py-6">
                  <div className="text-center">
                    <p className="text-4xl font-bold text-teal-400">
                      {stats?.post_processing?.msc ?? "—"}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">Completed</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* ── Row 4: Topics | Close Reasons | Missing Conversation Processing Jobs ── */}
          <section>
            <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
              Topic & Outcome Details
              {activePeriod && (
                <span className="ml-2 text-gray-600">
                  ({PERIOD_LABELS[activePeriod]})
                </span>
              )}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Topics */}
              <div className="card-glass p-4">
                <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-700">
                  <TagIcon className="w-4 h-4 text-indigo-400" />
                  <h3 className="text-sm font-semibold text-gray-200">
                    Topics Used
                  </h3>
                </div>
                <TopicsTable rows={stats?.topics?.[activePeriod || "2w"]} />
              </div>

              {/* Close Reasons */}
              <div className="card-glass p-4">
                <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-700">
                  <XCircleIcon className="w-4 h-4 text-rose-400" />
                  <h3 className="text-sm font-semibold text-gray-200">
                    Close Reasons
                  </h3>
                </div>
                <ClosedReasonsTable
                  rows={stats?.close_reasons?.[activePeriod || "2w"]}
                />
              </div>
              <div className="card-glass p-4">
                <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-700">
                  <BriefcaseIcon className="w-4 h-4 text-red-400" />
                  <h3 className="text-sm font-semibold text-gray-200">
                    Missed Conversation Processing Jobs
                  </h3>
                </div>
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-gray-400">
                      <th className="text-left py-1 font-medium">
                        Assignment ID
                      </th>
                      <th className=" py-1 font-medium">Job Name</th>
                      <th className="text-right py-1 font-medium">
                        Go to Session?
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats?.failed_jobs?.job_info?.map((p) => (
                      <tr
                        key={p}
                        className="border-t border-gray-800"
                        onClick={() =>
                          viewSessionWithErroredJobs(
                            p.job_parameters.assignment_id,
                          )
                        }
                      >
                        <td className="py-1.5 text-gray-400">
                          {p.job_parameters.assignment_id}
                        </td>
                        <td className="py-1.5 font-mono font-semibold text-gray-100">
                          {p.job_name}
                        </td>
                        <td className="py-1.5 text-right font-mono font-semibold text-gray-100">
                          <div className="flex justify-end">
                            <ArrowRightIcon className="w-4 h-4 text-gray-400 hover:text-blue-400 cursor-pointer transition-colors" />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        </>
      )}

      {loading && !stats && (
        <div className="flex items-center justify-center py-20 text-gray-500 text-sm">
          <ArrowPathIcon className="w-5 h-5 animate-spin mr-2" />
          Loading stats…
        </div>
      )}
    </div>
  );
}
